import type { PaginationDto, PaginationResponseDto } from "core/dto";
import { reorderByIds } from "core/helpers";
import { LOCALE } from "core/interfaces";
import type { PageMetaDto } from "shared/dto";

import { getComponentsDefault } from "~/common/defaults/components.default";
import { getPaginationDefault } from "~/common/defaults/pagination.default";
import { getPaginationResponseDefault } from "~/common/defaults/pagination-response.default";
import { getProductWithItemsDefault } from "~/common/defaults/product-with-items.default";
import type { LocaleQueryDto } from "~/common/interfaces/dto/locale-query.dto";
import type { GetAllShopProductsDto } from "~/common/interfaces/dto/shop/get-all-shop-products.dto";
import type { ShopComponentsResponseDto } from "~/common/interfaces/dto/shop/shop-components-response.dto";
import type { ShopInteractiveProductResponseDto } from "~/common/interfaces/dto/shop/shop-interactive-product-response.dto";
import type { ShopProductResponseDto } from "~/common/interfaces/dto/shop/shop-product-response.dto";
import type { ShopProductsCountResponseDto } from "~/common/interfaces/dto/shop/shop-products-count-response.dto";
import type { ShopProductsResponseDto } from "~/common/interfaces/dto/shop/shop-products-response.dto";

export const useShopStore = defineStore("shop", () => {
	const route = useRoute();
	const { locale } = useI18n();

	const components = ref<ShopComponentsResponseDto>(getComponentsDefault());
	const currentProduct = ref<ShopProductResponseDto>(getProductWithItemsDefault());
	const recentlyViewedProductIds = ref<number[]>([]);
	const recentlyViewedProducts = ref<ShopProductsResponseDto[]>([]);
	const products = ref<ShopProductsResponseDto[]>([]);
	const productsCount = ref<Record<string, number>>({});
	const bestSellingProducts = ref<ShopProductsResponseDto[]>([]);
	const productsMeta = ref<PageMetaDto>(getPaginationResponseDefault());
	const bestSellingProductsMeta = ref<PageMetaDto>(getPaginationResponseDefault());
	const interactiveProducts = ref<ShopInteractiveProductResponseDto[]>([]);
	const recentProductsQuery = ref<
		LocaleQueryDto & GetAllShopProductsDto & PaginationDto
	>();

	const getAllComponents = async () => {
		const res = await useNuxtApp().$apiFetch<ShopComponentsResponseDto>(
			"/shop/components",
			{
				query: { locale: locale.value as LOCALE }
			}
		);
		components.value = res;

		return res;
	};

	const getAllProducts = async (
		dto: GetAllShopProductsDto = {},
		paginationDto?: Partial<PaginationDto>,
		mergeProducts = false
	) => {
		recentProductsQuery.value = {
			...getPaginationDefault(),
			...paginationDto,
			...dto,
			locale: locale.value as LOCALE
		};

		const res = await useNuxtApp().$apiFetch<
			PaginationResponseDto<ShopProductsResponseDto>
		>("/shop/products", { query: recentProductsQuery.value });

		if (mergeProducts) {
			products.value.push(...res.data);
		} else {
			products.value = res.data;
		}
		productsMeta.value = res.meta;

		return res;
	};

	const getRecentlyViewedProducts = async () => {
		const query = {
			...getPaginationDefault(),
			limit: 4,
			locale: locale.value as LOCALE,
			ids: recentlyViewedProductIds.value
		};

		const res = await useNuxtApp().$apiFetch<
			PaginationResponseDto<ShopProductsResponseDto>
		>("/shop/products", { query });

		recentlyViewedProducts.value = reorderByIds(res.data, recentlyViewedProductIds.value);

		return res;
	};

	const getProductById = (id: number) => {
		return useNuxtApp().$apiFetch<ShopProductResponseDto>(`/shop/products/${id}`, { query: { locale: locale.value as LOCALE } });
	};

	const getBestSellingProducts = async () => {
		const query: LocaleQueryDto & Pick<GetAllShopProductsDto, "forFirstPage"> = {
			forFirstPage: true,
			locale: locale.value as LOCALE
		};

		const res = await useNuxtApp().$apiFetch<
			PaginationResponseDto<ShopProductsResponseDto>
		>("/shop/products", { query });
		bestSellingProducts.value = res.data;
		bestSellingProductsMeta.value = res.meta;

		return res;
	};

	const getInteractiveProducts = async () => {
		const query: LocaleQueryDto = {
			locale: locale.value as LOCALE
		};

		const res = await useNuxtApp().$apiFetch<PaginationResponseDto<ShopInteractiveProductResponseDto>>("/shop/products-interactive", { query });
		interactiveProducts.value = res.data;

		return res;
	};

	const getProductsCount = async () => {
		const res = await useNuxtApp().$apiFetch<ShopProductsCountResponseDto>("/shop/products-count");
		productsCount.value = res.count;

		return res;
	};

	const addToRecentlyViewed = ({ id }: ShopProductResponseDto) => {
		const index = recentlyViewedProductIds.value.indexOf(id);

		if (index > -1) {
			recentlyViewedProductIds.value.splice(index, 1);
		}

		if (recentlyViewedProductIds.value.length >= 4) {
			recentlyViewedProductIds.value.pop();
		}
		recentlyViewedProductIds.value.unshift(id);
	};

	watch(locale, async () => {
		getAllComponents();
		if (route.name === "shop-kind-product") {
			getRecentlyViewedProducts();
		}
		if (products.value.length && (route.name === "shop" || route.name === "shop-kind")) {
			getAllProducts();
		}
		if (bestSellingProducts.value.length && route.name === "index") {
			getBestSellingProducts();
		}
		if (interactiveProducts.value.length && route.name === "index") {
			getInteractiveProducts();
		}
		if (currentProduct.value.id > 0) {
			currentProduct.value = await getProductById(currentProduct.value.id);
		}
	});

	return {
		bestSellingProducts,
		bestSellingProductsMeta,
		components,
		currentProduct,
		interactiveProducts,
		products,
		productsCount,
		productsMeta,
		recentProductsQuery,
		recentlyViewedProductIds,
		recentlyViewedProducts,

		getAllComponents,
		getAllProducts,
		getBestSellingProducts,
		getInteractiveProducts,
		getProductById,
		getProductsCount,

		addToRecentlyViewed,
		getRecentlyViewedProducts
	};
},
{
	persist: {
		paths: ["recentlyViewedProductIds"],
		storage: persistedState.cookiesWithOptions({
			sameSite: true,
			maxAge: 2147483647
		})
	}
});
